import React, { useCallback, useEffect, useSyncExternalStore } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import NavItem, { NavItemType } from './components/NavItem';
import SessionDetailBackButton from './SessionDetailBackButton';
import ClipListPopover from './ClipListPopover';
import { NavigationEndpoint } from './types';
import { NavigationEndpointConfig } from './constants';
import { clearUnseenClipIds } from '@/stores/unseenClips';
import Loader from '@/components/atoms/Loader';
import { FeatureFlagKeys } from '@/services/featureFlag';
import featureFlagStore from '@/stores/featureFlagStore';
import AIDebugTools from '@/components/molecules/AIDebugTools/AIDebugTools';
import { CustomEvents } from '@/libs/eventBus/constants';
import EventBus from '@/libs/eventBus/eventBus';
import useSavedSearch from '@/hooks/useSavedSearch';
import ContentSettings from '@/components/molecules/ContentSettings';
import { useClipListPopover } from '@/hooks/useClipListPopover';
import { useRemoteApp } from '@/context/RemoteAppContext/RemoteAppContext';
import Icon from '@/components/atoms/Icon';

export default function SessionDetailLayout({ isLoading, navMenu }: { isLoading: boolean; navMenu: NavItemType[] }) {
  const { isRemoteApp } = useRemoteApp();
  const featureFlags = useSyncExternalStore(featureFlagStore.subscribe, featureFlagStore.getSnapshot);
  const { isClipListPopoverOpen } = useClipListPopover();
  const aiDebugToolsEnabled = featureFlags[FeatureFlagKeys.Use_CL_AI_Debug_Tools];
  const location = useLocation();
  const { isSavedSearchPage } = useSavedSearch();

  useEffect(() => {
    if (location.state?.previousPathname?.includes('/clips')) {
      clearUnseenClipIds();
    }
  }, [location.state?.previousPathname]);

  const openSettingsDrawer = useCallback(() => {
    EventBus.dispatch(CustomEvents.OpenContentSettings, { speakerId: null });
  }, []);

  const showHoverEffect = useCallback(
    (label: string) => {
      if (isClipListPopoverOpen && NavigationEndpointConfig[NavigationEndpoint.clips].label === label) {
        return true;
      }
      return false;
    },
    [isClipListPopoverOpen]
  );

  return (
    <div className={`z-40 flex h-full flex-col bg-slate-50 ${isRemoteApp ? '' : 'fixed inset-0'}`}>
      <div className="flex h-full grow gap-1">
        {aiDebugToolsEnabled && <AIDebugTools />}
        <div className="z-10 flex w-20 shrink-0 select-none flex-col items-center justify-between overflow-y-auto bg-slate-50 pt-[1.2rem] transition-all duration-[400ms]">
          <div className="flex flex-col space-y-2">
            {!isRemoteApp && <SessionDetailBackButton />}
            {navMenu.map(navItem => {
              return (
                <NavItem
                  navItem={{ ...navItem, iconContainerClassName: showHoverEffect(navItem.label) ? 'bg-slate-200' : '' }}
                  key={navItem.label}
                />
              );
            })}
          </div>
          <div className="flex flex-col items-center border-t border-t-slate-300/70 pb-5 pt-[0.45rem]">
            {!isSavedSearchPage && (
              <button
                onClick={openSettingsDrawer}
                data-testid="session-detail-settings-button"
                className="group flex flex-col items-center text-slate-600 hover:text-black"
              >
                <div className="flex size-9 items-center justify-center rounded-xl group-hover:bg-slate-200">
                  <Icon name="IconSettings" className="size-5" />
                </div>
                <div className="mt-0.5 text-xs">Settings</div>
              </button>
            )}
          </div>
        </div>
        <div className="h-full max-w-full grow">
          {isLoading ? (
            <div className="flex size-full items-center justify-center">
              <Loader />
            </div>
          ) : (
            <Outlet />
          )}
        </div>
      </div>
      {!isSavedSearchPage && <ContentSettings />}
      <ClipListPopover />
    </div>
  );
}
